import React from 'react';
import {createRoot, Root} from 'react-dom/client';
import * as Sentry from '@sentry/react';
import {StoreProvider} from 'easy-peasy';
import axios from 'axios';
import {enableMapSet, setAutoFreeze} from 'immer';
import {AlertProvider} from 'components/alerts';

import store from 'store';

import {CLIENT_ID} from 'utils/constants';

// Suppress passive event listener warnings for touch events
// This is needed because older jQuery plugins don't use passive listeners
const originalAddEventListener = EventTarget.prototype.addEventListener;
EventTarget.prototype.addEventListener = function (type, listener, options) {
    if (type === 'touchstart' && typeof options === 'object' && options !== null) {
        options.passive = true;
    }
    return originalAddEventListener.call(this, type, listener, options);
};

import {HeaderSearch} from 'components/header/header_search/HeaderSearch';
import EventDetailBlock from './views/event_detail/EventDetailBlock';
import SidebarMenu from './components/sidebar/SidebarMenu';
import BaseRouter from './routing/BaseRouter';
import ChatbotModal from './views/askai_chatbot/ChatbotModal';
import i18n from './i18n';

Sentry.init({
    dsn: window.FCONST.SENTRY_DSN,
    tracesSampler: () => Number(window.FCONST.FS_FRONTEND_SENTRY_TRACES_SAMPLE_RATE),
});

// Enable autofreeze globally
setAutoFreeze(true);

// Enable Map and Set support
enableMapSet();

axios.defaults.headers.common['X-Client-Id'] = CLIENT_ID;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.withCredentials = true;

// Set axios baseURL based on SITE_URL from backend configuration
// This allows the backend to control whether to use HTTP or HTTPS
if (window.FCONST?.SITE_URL) {
    axios.defaults.baseURL = window.FCONST.SITE_URL;
}

// Clean up axios parameters to prevent malformed URLs
axios.interceptors.request.use((config) => {
    // Remove empty string parameters that can cause malformed URLs
    if (config.params) {
        const cleanParams: any = {};
        for (const [key, value] of Object.entries(config.params)) {
            if (value !== '' && value !== null && value !== undefined) {
                cleanParams[key] = value;
            }
        }
        config.params = Object.keys(cleanParams).length > 0 ? cleanParams : undefined;
    }

    return config;
}, (error) => {
    return Promise.reject(error);
});

axios.interceptors.response.use((response) => response, (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || 'unknown';
    const method = (error?.config?.method || 'GET').toUpperCase();

    if (status >= 500) {
        console.error(`🚨 [API ${status}] ${method} ${url}`, error?.response?.data || error.message);
    } else if (status >= 400) {
        console.warn(`⚠️ [API ${status}] ${method} ${url}`, error?.response?.data || error.message);
    } else if (!status) {
        console.error(`🚨 [Network Error] ${method} ${url}`, error.message);
    }

    return Promise.reject(error);
});

interface EventData {
    id: number;
    event_id: number;
    event_text: string;
    event_agent_text: string;
    event_customer_text: string;
    event_type_name: string;
    eventTypeName: string;
    event_datetime: string;
    event_lang: string;
}

interface CONSTANTS {
    eventId: string;
    storyboards: Array<{slug: string}>;
}

declare const CONSTANTS: CONSTANTS;

interface ErrorFallbackProps {
    resetError: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({resetError}) => {

    return (
        <div className="fs-react-error-fallback">
            <img
                src="/static/theme/assets/images/fs_logo_horizontal_400x62.png"
                alt="Feelingstream"
                className="fs-react-error-fallback__logo"
            />
            <div className="fs-react-error-fallback__icon">
                !
            </div>
            <h3 className="fs-react-error-fallback__title">
                {i18n.t('jt_error_page_title.text')}
            </h3>
            <p className="fs-react-error-fallback__description">
                {i18n.t('jt_error_page_description.text')}
            </p>
            <div className="fs-react-error-fallback__actions">
                <button
                    onClick={resetError}
                    className="btn btn-primary fs-react-error-fallback__button-primary"
                    data-cy="main-btn"
                >
                    {i18n.t('bt_try_again.text')}
                </button>
            </div>
        </div>
    );
};

// Store React 18 roots to avoid creating multiple roots for the same container
const rootsMap = new Map<string, Root>();

function renderComponent(containerId: string, componentFactory: () => React.ReactElement): void {
    const elem = document.getElementById(containerId);
    if (!elem) {
        return;
    }

    // Check if we already have a root for this container
    let root = rootsMap.get(containerId);
    if (!root) {
        root = createRoot(elem);
        rootsMap.set(containerId, root);
    }

    root.render(
        <StoreProvider store={store}>
            <AlertProvider>
                <Sentry.ErrorBoundary
                    fallback={(props) => <ErrorFallback {...(props as any)} />}
                    onError={(error, errorInfo) => {
                        console.error('🚨 React Error Boundary caught an error:', error);
                        console.error('📍 Error Info:', errorInfo);
                        console.error('📊 Component Stack:', (errorInfo as any)?.componentStack || 'No component stack');
                    }}
                >
                    {componentFactory()}
                </Sentry.ErrorBoundary>
            </AlertProvider>
        </StoreProvider>
    );
}

function initSidebarMenu(): void {
    renderComponent('sidebar-menu', () => <SidebarMenu />);
}

function initEventDetailBlock(): void {
    renderComponent('event-detail-block', () => <EventDetailBlock activeEventId={parseInt(window.CONSTANTS.eventId, 10)} />);
}

function initHeaderSearchBlock(): void {
    renderComponent('header-search-block', () => <HeaderSearch />);
}


function initBaseRouter(): void {
    renderComponent('spa', () => <BaseRouter />);
}

function initChatBotModal() {
    const modules = (window.FCONST as any).MODULES;
    if (modules?.['addon-ask-ai'] === true) {
        renderComponent('chat-bot-modal', () => <ChatbotModal />);
    }
}


export {
    initSidebarMenu,
    initEventDetailBlock,
    initHeaderSearchBlock,
    initBaseRouter,
    initChatBotModal
};

// Make functions available globally for template usage
declare global {
    interface Window {
        fsvision: {
            initSidebarMenu: () => void;
            initEventDetailBlock: () => void;
            initHeaderSearchBlock: () => void;
            initBaseRouter: () => void;
            initChatBotModal: () => void;
        };
    }
}

// Attach functions to global fsvision object
window.fsvision = window.fsvision || {};
Object.assign(window.fsvision, {
    initSidebarMenu,
    initEventDetailBlock,
    initHeaderSearchBlock,
    initBaseRouter,
    initChatBotModal
});
